import { trpc } from './trpc';
import { useToast } from '@/components/ui/use-toast';

/**
 * Hooks for the Apple Pay **platform integrator (PPIID)** API — the staff-only
 * surface behind `/manage/apple-pay`.
 *
 * Separate from `apple-pay-registrations.ts` (BYOK, merchant + AM surfaces) and
 * from `apple-pay-domains.ts` (the legacy platform-managed domain pool). Nothing
 * here can affect a BYOK merchant.
 */

type Environment = 'production' | 'sandbox';

export function useApplePayIntegrator(environment: Environment) {
  return trpc.applePayPlatform.getIntegrator.useQuery(
    { environment },
    { staleTime: 30 * 1000 },
  );
}

export function useApplePayPlatformDomains(environment: Environment) {
  return trpc.applePayPlatform.listDomains.useQuery({ environment }, { staleTime: 30 * 1000 });
}

/**
 * Shared invalidation: every mutation below can change both the integrator
 * status and the domain list, so refresh both rather than guessing.
 */
function useInvalidatePlatform() {
  const utils = trpc.useUtils();
  return () => {
    void utils.applePayPlatform.getIntegrator.invalidate();
    void utils.applePayPlatform.listDomains.invalidate();
  };
}

export function useUpsertApplePayIntegrator() {
  const { toast } = useToast();
  const invalidate = useInvalidatePlatform();
  return trpc.applePayPlatform.upsertIntegrator.useMutation({
    onSuccess: () => {
      invalidate();
      toast({ title: 'Platform integrator saved' });
    },
    onError: (error: { message: string }) => {
      toast({ title: 'Save failed', description: error.message, variant: 'destructive' });
    },
  });
}

export function useSetApplePayIntegratorStatus() {
  const { toast } = useToast();
  const invalidate = useInvalidatePlatform();
  return trpc.applePayPlatform.setIntegratorStatus.useMutation({
    onSuccess: () => invalidate(),
    onError: (error: { message: string }) => {
      toast({ title: 'Update failed', description: error.message, variant: 'destructive' });
    },
  });
}

export function useAddApplePayPlatformDomain() {
  const { toast } = useToast();
  const invalidate = useInvalidatePlatform();
  return trpc.applePayPlatform.addDomain.useMutation({
    onSuccess: () => {
      invalidate();
      toast({ title: 'Domain added' });
    },
    onError: (error: { message: string }) => {
      toast({ title: 'Could not add domain', description: error.message, variant: 'destructive' });
    },
  });
}

/**
 * Probe never rejects on a failed check — it reports what Apple would see, so
 * the toast reflects the probe result rather than a thrown error.
 */
export function useProbeApplePayPlatformDomain() {
  const { toast } = useToast();
  const invalidate = useInvalidatePlatform();
  return trpc.applePayPlatform.probeDomain.useMutation({
    onSuccess: (result: { matches: boolean; error?: string }) => {
      invalidate();
      toast({
        title: result.matches ? 'Association file verified' : 'Association file not verified',
        description: result.matches ? undefined : result.error,
        variant: result.matches ? undefined : 'destructive',
      });
    },
    onError: (error: { message: string }) => {
      toast({ title: 'Check failed', description: error.message, variant: 'destructive' });
    },
  });
}

/**
 * Read-only: reports on the domain without changing it. A mutation rather than a
 * query because it hits the live web and both funnel snapshots, which is too
 * expensive to run on every render of the table.
 */
export function useDiagnoseApplePayPlatformDomain() {
  const { toast } = useToast();
  return trpc.applePayPlatform.diagnoseDomain.useMutation({
    onError: (error: { message: string }) => {
      toast({ title: 'Diagnosis failed', description: error.message, variant: 'destructive' });
    },
  });
}

/**
 * Registration failures are recorded on the row rather than thrown, so a
 * `failed` status still arrives through `onSuccess`.
 */
export function useRegisterApplePayPlatformDomain() {
  const { toast } = useToast();
  const invalidate = useInvalidatePlatform();
  return trpc.applePayPlatform.registerDomain.useMutation({
    onSuccess: (domain: { status: string; lastError?: string | null }) => {
      invalidate();
      if (domain.status === 'registered') {
        toast({ title: 'Domain registered with Apple' });
      } else {
        toast({
          title: 'Apple rejected the registration',
          description: domain.lastError ?? undefined,
          variant: 'destructive',
        });
      }
    },
    onError: (error: { message: string }) => {
      toast({ title: 'Registration failed', description: error.message, variant: 'destructive' });
    },
  });
}

export function useUnregisterApplePayPlatformDomain() {
  const { toast } = useToast();
  const invalidate = useInvalidatePlatform();
  return trpc.applePayPlatform.unregisterDomain.useMutation({
    onSuccess: () => {
      invalidate();
      toast({ title: 'Domain unregistered at Apple' });
    },
    onError: (error: { message: string }) => {
      toast({ title: 'Unregister failed', description: error.message, variant: 'destructive' });
    },
  });
}

export function useSetApplePayPlatformDomainActive() {
  const { toast } = useToast();
  const invalidate = useInvalidatePlatform();
  return trpc.applePayPlatform.setDomainActive.useMutation({
    onSuccess: () => invalidate(),
    onError: (error: { message: string }) => {
      toast({ title: 'Update failed', description: error.message, variant: 'destructive' });
    },
  });
}

export function useDeleteApplePayPlatformDomain() {
  const { toast } = useToast();
  const invalidate = useInvalidatePlatform();
  return trpc.applePayPlatform.deleteDomain.useMutation({
    onSuccess: () => {
      invalidate();
      toast({ title: 'Domain deleted' });
    },
    onError: (error: { message: string }) => {
      toast({ title: 'Delete failed', description: error.message, variant: 'destructive' });
    },
  });
}
